import React from 'react';
import PropTypes from 'prop-types';
import { Table, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

const RESERVED_KEYS = [
  'title',
  'description',
  'files',
  'id',
  'created',
  'created_by'
];
const CONDITION_OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'does not equal' },
  { value: 'filled', label: 'is filled' },
  { value: 'empty', label: 'is empty' }
];

const FieldBuilder = ({ fields, onChange, isEditMode = false }) => {
  const buildTriggerFields = (sourceFields, index) =>
    sourceFields.slice(0, index).map((field, fieldIndex) => ({
      field_key: field.field_key || `field_${fieldIndex + 1}`,
      label: field.label || `Field ${fieldIndex + 1}`,
      type: field.type,
      options: field.options || []
    }));

  const getTriggerFields = index =>
    buildTriggerFields(fields, index);

  const updateConditionReferences = (
    sourceFields,
    sourceIndex,
    previousKey,
    nextKey
  ) => {
    if (!previousKey || previousKey === nextKey) return sourceFields;

    return sourceFields.map((dependentField, dependentIdx) => {
      if (
        dependentIdx <= sourceIndex ||
        !dependentField.conditions?.rules?.length
      ) {
        return dependentField;
      }

      return {
        ...dependentField,
        conditions: {
          ...dependentField.conditions,
          rules: dependentField.conditions.rules.map(rule =>
            rule.field_key === previousKey
              ? { ...rule, field_key: nextKey }
              : rule
          )
        }
      };
    });
  };

  const getDefaultConditionRule = index => {
    const triggerFields = getTriggerFields(index);
    const triggerField = triggerFields[0];
    return {
      field_key: triggerField?.field_key || '',
      operator: 'eq',
      value:
        triggerField?.type === 'checkbox'
          ? true
          : triggerField?.options?.[0] || ''
    };
  };

  const getRuleValueOptions = triggerField => {
    if (!triggerField) return [];
    if (triggerField.type === 'checkbox') {
      return [
        { value: true, label: 'Checked' },
        { value: false, label: 'Unchecked' }
      ];
    }
    if (['select', 'radio'].includes(triggerField.type)) {
      return (triggerField.options || []).map(option => ({
        value: option,
        label: option
      }));
    }
    return [];
  };

  const handleAddField = () => {
    const newField = {
      field_key: '',
      label: '',
      type: 'text',
      required: false,
      options: [],
      sort_order: fields.length + 1,
      _isNew: true // helper flag to indicate if it can edit field_key
    };
    onChange([...fields, newField]);
  };

  const handleRemoveField = index => {
    const removedKey = fields[index]?.field_key;
    const updated = fields
      .filter((_, idx) => idx !== index)
      .map((f, idx) => {
        const rules = (f.conditions?.rules || []).filter(
          rule => rule.field_key !== removedKey
        );
        return {
          ...f,
          sort_order: idx + 1,
          conditions: rules.length ? { ...f.conditions, rules } : null
        };
      });
    onChange(updated);
  };

  const handleFieldChange = (index, key, val) => {
    const updated = [...fields];
    const field = { ...updated[index] };

    if (key === 'label') {
      const previousKey = field.field_key;
      field.label = val;
      // Auto-suggest snake_case field_key if it's a new field
      if (field._isNew && !field.field_key_custom) {
        field.field_key = val
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/(^_+|_+$)/g, '');
      }
      if (field.field_key) {
        updated[index] = field;
        onChange(
          updateConditionReferences(updated, index, previousKey, field.field_key)
        );
        return;
      }
    } else if (key === 'field_key') {
      const previousKey = field.field_key;
      field.field_key = val.toLowerCase().replace(/[^a-z0-9_]/g, '');
      field.field_key_custom = true; // flag to stop auto-suggesting
      updated[index] = field;
      onChange(
        updateConditionReferences(updated, index, previousKey, field.field_key)
      );
      return;
    } else if (key === 'options') {
      // Expect val to be comma-separated string, convert to array
      field._optionsString = val;
      field.options = val
        .split(',')
        .map(o => o.trim())
        .filter(o => o !== '');
    } else if (key === 'type') {
      field.type = val;
      if (!['select', 'radio'].includes(val)) {
        field.options = [];
        field._optionsString = '';
      }
    } else {
      field[key] = val;
    }

    updated[index] = field;
    onChange(updated);
  };

  const moveField = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === fields.length - 1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const updated = [...fields];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;

    // Reset sort order
    const reordered = updated.map((f, idx) => ({ ...f, sort_order: idx + 1 }));
    onChange(reordered);
  };

  const handleAddCondition = index => {
    const updated = fields.map((field, fieldIndex) =>
      fieldIndex < index && !field.field_key
        ? { ...field, field_key: `field_${fieldIndex + 1}` }
        : field
    );
    const triggerFields = buildTriggerFields(updated, index);
    if (!triggerFields.length) {
      toast.info('Add a field above this one before adding a condition.');
      return;
    }

    const field = { ...updated[index] };
    field.conditions = {
      logic: field.conditions?.logic || 'AND',
      rules: [
        ...(field.conditions?.rules || []),
        {
          field_key: triggerFields[0].field_key,
          operator: 'eq',
          value:
            triggerFields[0].type === 'checkbox'
              ? true
              : triggerFields[0].options?.[0] || ''
        }
      ]
    };
    updated[index] = field;
    onChange(updated);
  };

  const handleRemoveCondition = (fieldIndex, ruleIndex) => {
    const updated = [...fields];
    const field = { ...updated[fieldIndex] };
    const rules = (field.conditions?.rules || []).filter(
      (_, idx) => idx !== ruleIndex
    );
    field.conditions = rules.length ? { ...field.conditions, rules } : null;
    updated[fieldIndex] = field;
    onChange(updated);
  };

  const handleConditionLogicChange = (fieldIndex, logic) => {
    const updated = [...fields];
    const field = { ...updated[fieldIndex] };
    field.conditions = {
      logic,
      rules: field.conditions?.rules || [getDefaultConditionRule(fieldIndex)]
    };
    updated[fieldIndex] = field;
    onChange(updated);
  };

  const handleConditionRuleChange = (fieldIndex, ruleIndex, key, value) => {
    const updated = [...fields];
    const field = { ...updated[fieldIndex] };
    const rules = [...(field.conditions?.rules || [])];
    const rule = { ...rules[ruleIndex] };
    const triggerFields = getTriggerFields(fieldIndex);

    if (key === 'field_key') {
      const triggerField = triggerFields.find(f => f.field_key === value);
      rule.field_key = value;
      rule.value =
        triggerField?.type === 'checkbox'
          ? true
          : triggerField?.options?.[0] || '';
    } else if (key === 'operator') {
      rule.operator = value;
      if (['filled', 'empty'].includes(value)) {
        delete rule.value;
      } else if (rule.value === undefined) {
        const triggerField = triggerFields.find(
          f => f.field_key === rule.field_key
        );
        rule.value =
          triggerField?.type === 'checkbox'
            ? true
            : triggerField?.options?.[0] || '';
      }
    } else {
      rule[key] = value;
    }

    rules[ruleIndex] = rule;
    field.conditions = {
      logic: field.conditions?.logic || 'AND',
      rules
    };
    updated[fieldIndex] = field;
    onChange(updated);
  };

  const validateFieldKeys = () => {
    const keys = fields.map(f => f.field_key);
    const duplicates = keys.filter(
      (item, index) => keys.indexOf(item) !== index
    );

    if (duplicates.length > 0) {
      toast.error(`Duplicate field keys found: ${duplicates.join(', ')}`);
      return false;
    }

    const reserved = keys.filter(k => RESERVED_KEYS.includes(k));
    if (reserved.length > 0) {
      toast.error(
        `Reserved words cannot be used as field keys: ${reserved.join(', ')}`
      );
      return false;
    }

    return true;
  };

  const renderConditionValueControl = (fieldIndex, ruleIndex, rule) => {
    if (['filled', 'empty'].includes(rule.operator)) {
      return <Form.Control size="sm" value="No value needed" disabled />;
    }

    const triggerField = getTriggerFields(fieldIndex).find(
      field => field.field_key === rule.field_key
    );
    const valueOptions = getRuleValueOptions(triggerField);

    if (valueOptions.length) {
      return (
        <Form.Select
          size="sm"
          value={String(rule.value ?? '')}
          onChange={e => {
            const selected = valueOptions.find(
              option => String(option.value) === e.target.value
            );
            handleConditionRuleChange(
              fieldIndex,
              ruleIndex,
              'value',
              selected ? selected.value : e.target.value
            );
          }}
        >
          {valueOptions.map(option => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      );
    }

    return (
      <Form.Control
        type="text"
        size="sm"
        value={rule.value || ''}
        onChange={e =>
          handleConditionRuleChange(
            fieldIndex,
            ruleIndex,
            'value',
            e.target.value
          )
        }
        placeholder="Expected value"
      />
    );
  };

  // Expose validator
  React.useImperativeHandle(React.Ref, () => ({
    validate: validateFieldKeys
  }));

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Parameter Fields</h6>
        <Button variant="outline-primary" size="sm" onClick={handleAddField}>
          <FontAwesomeIcon icon="plus" className="me-1" /> Add Field
        </Button>
      </div>

      {fields.length === 0 ? (
        <div className="text-center border rounded py-4 bg-light text-500 fs-10">
          No parameter fields added yet. Add a field to start building the
          schema.
        </div>
      ) : (
        <Table
          responsive
          bordered
          hover
          size="sm"
          className="fs-10 align-middle"
        >
          <thead className="bg-200 text-900">
            <tr>
              <th style={{ width: '5%' }} className="text-center">
                #
              </th>
              <th style={{ width: '25%' }}>Field Label</th>
              <th style={{ width: '25%' }}>Field Key (snake_case)</th>
              <th style={{ width: '20%' }}>Input Type</th>
              <th style={{ width: '10%' }} className="text-center">
                Required
              </th>
              <th style={{ width: '15%' }} className="text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, idx) => {
              const isKeyDisabled = isEditMode && !field._isNew;
              const hasOptions = ['select', 'radio'].includes(field.type);
              const optionsVal =
                field._optionsString !== undefined
                  ? field._optionsString
                  : (field.options || []).join(', ');
              const conditionRules = field.conditions?.rules || [];
              const triggerFields = getTriggerFields(idx);

              return (
                <React.Fragment key={idx}>
                  <tr>
                    <td className="text-center fw-semi-bold">{idx + 1}</td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={field.label || ''}
                        onChange={e =>
                          handleFieldChange(idx, 'label', e.target.value)
                        }
                        placeholder="e.g. Blood Group"
                        required
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={field.field_key || ''}
                        onChange={e =>
                          handleFieldChange(idx, 'field_key', e.target.value)
                        }
                        placeholder="e.g. blood_group"
                        disabled={isKeyDisabled}
                        required
                      />
                    </td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={field.type || 'text'}
                        onChange={e =>
                          handleFieldChange(idx, 'type', e.target.value)
                        }
                      >
                        <option value="text">Text Input</option>
                        <option value="textarea">Text Area</option>
                        <option value="number">Number</option>
                        <option value="date">Date Picker</option>
                        <option value="select">Dropdown Select</option>
                        <option value="radio">Radio Options</option>
                        <option value="checkbox">Checkbox Toggle</option>
                        <option value="file">File Upload</option>
                        <option value="file_or_text">
                          File or Text Toggle
                        </option>
                      </Form.Select>
                    </td>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        id={`required-check-${idx}`}
                        checked={!!field.required}
                        onChange={e =>
                          handleFieldChange(idx, 'required', e.target.checked)
                        }
                        className="d-inline-block"
                      />
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-1">
                        <Button
                          variant="light"
                          size="sm"
                          className="p-1 px-2"
                          disabled={idx === 0}
                          onClick={() => moveField(idx, 'up')}
                        >
                          <FontAwesomeIcon icon="arrow-up" />
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          className="p-1 px-2"
                          disabled={idx === fields.length - 1}
                          onClick={() => moveField(idx, 'down')}
                        >
                          <FontAwesomeIcon icon="arrow-down" />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="p-1 px-2"
                          onClick={() => handleRemoveField(idx)}
                        >
                          <FontAwesomeIcon icon="trash" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {hasOptions && (
                    <tr className="bg-light">
                      <td></td>
                      <td colSpan={5} className="py-2">
                        <Row className="align-items-center">
                          <Col xs="auto" className="fw-semi-bold text-600">
                            Configure Options:
                          </Col>
                          <Col>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={optionsVal}
                              onChange={e =>
                                handleFieldChange(
                                  idx,
                                  'options',
                                  e.target.value
                                )
                              }
                              placeholder="Comma-separated options (e.g. Option A, Option B, Option C)"
                              required
                            />
                          </Col>
                        </Row>
                      </td>
                    </tr>
                  )}
                  <tr className="bg-light">
                    <td></td>
                    <td colSpan={5} className="py-2">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="fw-semi-bold text-primary">
                          <FontAwesomeIcon icon="eye" className="me-1" />
                          Show this field only when...
                          {conditionRules.length > 0 && (
                            <Badge bg="info" className="ms-2">
                              {conditionRules.length} rule
                              {conditionRules.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        <div className="d-flex gap-2">
                          {conditionRules.length > 1 && (
                            <Form.Select
                              size="sm"
                              value={field.conditions?.logic || 'AND'}
                              onChange={e =>
                                handleConditionLogicChange(idx, e.target.value)
                              }
                              style={{ width: '90px' }}
                            >
                              <option value="AND">AND</option>
                              <option value="OR">OR</option>
                            </Form.Select>
                          )}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleAddCondition(idx)}
                          >
                            <FontAwesomeIcon icon="plus" className="me-1" />
                            Add condition
                          </Button>
                        </div>
                      </div>

                      {!triggerFields.length && (
                        <div className="text-500 fs-11">
                          Add a radio, checkbox, dropdown, or another field
                          above this one to use it as a trigger.
                        </div>
                      )}

                      {conditionRules.map((rule, ruleIdx) => (
                        <Row
                          key={ruleIdx}
                          className="g-2 align-items-center mb-2"
                        >
                          <Col md={4}>
                            <Form.Select
                              size="sm"
                              value={rule.field_key || ''}
                              onChange={e =>
                                handleConditionRuleChange(
                                  idx,
                                  ruleIdx,
                                  'field_key',
                                  e.target.value
                                )
                              }
                            >
                              {triggerFields.map(triggerField => (
                                <option
                                  key={triggerField.field_key}
                                  value={triggerField.field_key}
                                >
                                  {triggerField.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={3}>
                            <Form.Select
                              size="sm"
                              value={rule.operator || 'eq'}
                              onChange={e =>
                                handleConditionRuleChange(
                                  idx,
                                  ruleIdx,
                                  'operator',
                                  e.target.value
                                )
                              }
                            >
                              {CONDITION_OPERATORS.map(operator => (
                                <option
                                  key={operator.value}
                                  value={operator.value}
                                >
                                  {operator.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={4}>
                            {renderConditionValueControl(idx, ruleIdx, rule)}
                          </Col>
                          <Col md={1} className="text-end">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() =>
                                handleRemoveCondition(idx, ruleIdx)
                              }
                              title="Remove condition"
                            >
                              <FontAwesomeIcon icon="times" />
                            </Button>
                          </Col>
                        </Row>
                      ))}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
};

FieldBuilder.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      field_key: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string,
      required: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.string),
      sort_order: PropTypes.number,
      conditions: PropTypes.shape({
        logic: PropTypes.oneOf(['AND', 'OR']),
        rules: PropTypes.arrayOf(
          PropTypes.shape({
            field_key: PropTypes.string,
            operator: PropTypes.string,
            value: PropTypes.any
          })
        )
      })
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool
};

export default FieldBuilder;
export { RESERVED_KEYS };
